import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, rupiahShort } from "@/lib/format";
import { ClickLeaderboard } from "@/components/admin/ClickLeaderboard";
import { motion } from "framer-motion";
import {
  TrendingUp, Wallet, Users, Clock, Banknote, ArrowUpRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const CHART_COLORS = ["#6366F1", "#06B6D4", "#F59E0B", "#10B981", "#F43F5E"];

function KpiCard({ icon: Icon, label, value, sub, accent, delay, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      data-testid={testid}
      className="bg-card border border-border rounded-xl p-5 relative overflow-hidden hover:border-gray-700 transition-colors"
    >
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl opacity-20`} style={{ background: accent }} />
      <div className="flex items-center justify-between mb-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${accent}22` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className="font-display text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Memuat dashboard...</div>;

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="admin-dashboard-container">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Dashboard Agency</h1>
        <p className="text-muted-foreground mt-1">Ringkasan performa afiliasi & disbursement Komisia</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <KpiCard testid="kpi-gmv" icon={TrendingUp} label="Total GMV Afiliasi" value={rupiahShort(data.total_gmv)} sub={rupiah(data.total_gmv)} accent="#06B6D4" delay={0.02} />
        <KpiCard testid="kpi-agency-revenue" icon={Banknote} label="Revenue Agency" value={rupiahShort(data.total_agency_revenue)} sub={`Margin ${data.agency_margin_pct}%`} accent="#10B981" delay={0.06} />
        <KpiCard testid="kpi-talent-commission" icon={Wallet} label="Komisi Talent" value={rupiahShort(data.total_talent_commission)} sub={rupiah(data.total_talent_commission)} accent="#F59E0B" delay={0.1} />
        <KpiCard testid="kpi-pending-payout" icon={Clock} label="Payout Tertunda" value={String(data.pending_payout_count)} sub={rupiah(data.pending_payout_amount)} accent="#F43F5E" delay={0.14} />
        <KpiCard testid="kpi-active-talents" icon={Users} label="Talent Aktif" value={String(data.active_talents)} sub={`Disbursed ${rupiahShort(data.total_disbursed)}`} accent="#6366F1" delay={0.18} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Tren GMV vs Revenue Agency</h3>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><ArrowUpRight className="h-3 w-3" /> Bulanan</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.monthly_trend}>
              <defs>
                <linearGradient id="gGmv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gAgency" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="month" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} tickFormatter={rupiahShort} width={70} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
              <Area type="monotone" dataKey="gmv" stroke="#06B6D4" fill="url(#gGmv)" strokeWidth={2} name="GMV" />
              <Area type="monotone" dataKey="agency" stroke="#10B981" fill="url(#gAgency)" strokeWidth={2} name="Revenue Agency" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-4">Distribusi GMV per Platform</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.platform_breakdown} dataKey="gmv" nameKey="platform" cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {data.platform_breakdown.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#0B0F17" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4">Top 5 Talent berdasarkan Komisi</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.top_talents} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
            <XAxis type="number" stroke="#6B7280" fontSize={11} tickFormatter={rupiahShort} />
            <YAxis type="category" dataKey="name" stroke="#9CA3AF" fontSize={12} width={110} />
            <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => rupiah(v)} cursor={{ fill: "#1F2937" }} />
            <Bar dataKey="commission" fill="#6366F1" radius={[0, 6, 6, 0]} name="Komisi" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ClickLeaderboard rows={data.click_leaderboard || []} daily={data.daily_clicks} totalClicks={data.total_clicks || 0} />
    </div>
  );
}
